import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { ErrorState } from '@/components/ui/States';
import { Badge } from '@/components/ui/Badge';
import { keys } from '@/lib/queryKeys';
import { searchPatients } from '@/features/patients/api';
import type { Patient } from '@/features/patients/types';
import TestSelector, { type SelectedTests } from './TestSelector';
import { useCreateLabOrder } from './hooks';
import type { LabPriority } from './types';

/**
 * Raising a laboratory order.
 *
 * Reuses the EXISTING patient directory (`searchPatients`, the same endpoint
 * registration and the pharmacy counter search) rather than a laboratory
 * patient list — there is one patient master in this system and this screen
 * does not get a second one.
 *
 * The encounter is required, not optional: an order with no visit or
 * admission behind it has nothing to bill against, no ordering context and
 * nowhere for the result to come back to. The server enforces it too (a CHECK
 * constraint plus an explicit validation), so this is the polite version of a
 * rule that holds regardless.
 */

export interface NewLabOrderDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pre-selected when opened from a visit/consultation rather than the lab. */
  patient?: Pick<Patient, 'id' | 'name' | 'uhid' | 'age' | 'gender'> | null;
  visitId?: number | null;
  admissionId?: number | null;
  consultationId?: number | null;
  /** The Emergency workspace opens this with the branch's ED default ('stat', design §3.3). */
  defaultPriority?: LabPriority;
  /** Set by the Emergency workspace while the patient is still in the ED: the
   *  order is charged to the encounter and settles with the ED bill (or the IP
   *  bill if admitted), so no separate invoice is offered. */
  emergency?: boolean;
  onCreated?: (orderId: number) => void;
}

export default function NewLabOrderDialog({
  open, onClose, patient: presetPatient, visitId, admissionId, consultationId, defaultPriority = 'routine', emergency = false, onCreated,
}: NewLabOrderDialogProps) {
  // Ward and ED orders accrue to the stay and settle with its bill — see
  // utils/labIpCharges.js. Offering "raise a bill now" there bills the test twice
  // (ward) or leaves it on an invoice the admission never sees (ED).
  const accruesToStay = !!admissionId || emergency;
  const [patient, setPatient] = useState<NewLabOrderDialogProps['patient']>(presetPatient ?? null);
  const [search, setSearch] = useState('');
  const [selection, setSelection] = useState<SelectedTests>({ testIds: [], packageIds: [] });
  const [priority, setPriority] = useState<LabPriority>(defaultPriority);
  const [clinicalNotes, setClinicalNotes] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [createBill, setCreateBill] = useState(!accruesToStay);
  const [paymentStatus, setPaymentStatus] = useState<'Paid' | 'Pending'>('Pending');
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [error, setError] = useState<string | null>(null);

  const create = useCreateLabOrder();

  useEffect(() => {
    if (open) {
      setPatient(presetPatient ?? null);
      setSelection({ testIds: [], packageIds: [] });
      setPriority(defaultPriority);
      setClinicalNotes(''); setDiagnosis('');
      setCreateBill(!accruesToStay);
      setError(null);
      setSearch('');
    }
  }, [open, presetPatient, accruesToStay, defaultPriority]);

  const results = useQuery({
    queryKey: keys.patients.search(search.trim()),
    queryFn: () => searchPatients(search.trim()),
    enabled: open && !presetPatient && search.trim().length >= 2,
  });

  const nothingSelected = selection.testIds.length === 0 && selection.packageIds.length === 0;
  const noEncounter = !visitId && !admissionId;

  const submit = async () => {
    setError(null);
    if (!patient) { setError('Choose a patient first.'); return; }
    if (noEncounter) {
      setError('This order needs a visit or an admission behind it. Start it from the patient’s visit or ward record.');
      return;
    }
    if (nothingSelected) { setError('Select at least one test.'); return; }
    try {
      const order = await create.mutateAsync({
        patient_id: patient.id,
        visit_id: visitId ?? null,
        admission_id: admissionId ?? null,
        consultation_id: consultationId ?? null,
        priority,
        clinical_notes: clinicalNotes.trim() || undefined,
        diagnosis: diagnosis.trim() || undefined,
        tests: selection.testIds.map((test_id) => ({ test_id })),
        packages: selection.packageIds.map((package_id) => ({ package_id })),
        create_bill: emergency ? false : createBill,
        payment_mode: createBill && !emergency ? paymentMode : undefined,
        payment_status: createBill && !emergency ? paymentStatus : undefined,
      });
      onCreated?.(order.id);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>New laboratory order</DialogTitle>
          <DialogDescription>
            The charge is raised through the hospital’s existing billing engine — this is an ordinary
            invoice, not a separate laboratory bill.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[65vh] space-y-4 overflow-y-auto pr-1">
          <FormField label="Patient" required>
            {patient ? (
              <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                <span className="text-[13px]">
                  <span className="font-semibold">{patient.name}</span>
                  <span className="text-muted-foreground"> · {patient.uhid} · {patient.age ?? '—'}
                    {patient.age ? 'y' : ''}, {patient.gender}</span>
                </span>
                {!presetPatient && (
                  <Button variant="link" size="sm" onClick={() => setPatient(null)}>Change</Button>
                )}
              </div>
            ) : (
              <div>
                <Input
                  autoFocus
                  placeholder="Search by name, mobile or UHID…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {results.data && results.data.length > 0 && (
                  <div className="mt-1 max-h-40 overflow-auto rounded-lg border border-border">
                    {results.data.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        className="block w-full border-b border-border px-3 py-1.5 text-left text-[13px] last:border-0 hover:bg-muted"
                        onClick={() => setPatient(p)}
                      >
                        <span className="font-medium">{p.name}</span>
                        <span className="text-muted-foreground"> · {p.uhid} · {p.mobile}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </FormField>

          {noEncounter && (
            <div className="rounded-lg border border-warn-soft bg-warn-soft px-3 py-2 text-[12px] text-warn-ink">
              No visit or admission is attached. A laboratory order must belong to an encounter —
              open this from the patient’s visit workflow or their ward record.
            </div>
          )}

          <FormField label="Tests" required>
            <TestSelector value={selection} onChange={setSelection} />
          </FormField>

          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Priority">
              <select
                className="h-9 w-full rounded-lg border border-border bg-card px-2 text-[13px]"
                value={priority}
                onChange={(e) => setPriority(e.target.value as LabPriority)}
              >
                <option value="routine">Routine</option>
                <option value="urgent">Urgent</option>
                <option value="stat">STAT</option>
              </select>
            </FormField>
            <FormField label="Provisional diagnosis">
              <Input value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)} placeholder="Optional" />
            </FormField>
          </div>

          <FormField label="Clinical notes for the laboratory">
            <Textarea
              rows={2}
              value={clinicalNotes}
              onChange={(e) => setClinicalNotes(e.target.value)}
              placeholder="Anything the laboratory needs to interpret this — current treatment, suspected diagnosis…"
            />
          </FormField>

          {emergency ? (
            <div className="rounded-lg border border-border px-3 py-2 text-[12px] text-muted-foreground">
              <Badge variant="info">Emergency</Badge>{' '}
              Charged to this emergency visit. It settles with the ED bill — or moves to the IP bill if the
              patient is admitted.
            </div>
          ) : (
          <div className="rounded-lg border border-border px-3 py-2">
            <label className="flex items-center gap-2 text-[13px]">
              <input type="checkbox" checked={createBill} onChange={(e) => setCreateBill(e.target.checked)} />
              Raise the charge now
              {admissionId && <Badge variant="info">In-patient</Badge>}
            </label>
            {admissionId ? (
              <p className="mt-1 text-[11px] text-muted-foreground">
                An in-patient test normally accrues to the admission and settles at discharge — leave this
                off unless this test is being billed separately.
              </p>
            ) : (
              <p className="mt-1 text-[11px] text-muted-foreground">
                Creates an ordinary OPD invoice in the same transaction as the order, with the hospital’s
                own GST settings and invoice series.
              </p>
            )}
            {createBill && (
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                <select
                  className="h-8 rounded-lg border border-border bg-card px-2 text-[12px]"
                  value={paymentStatus}
                  onChange={(e) => setPaymentStatus(e.target.value as 'Paid' | 'Pending')}
                >
                  <option value="Pending">Payment pending</option>
                  <option value="Paid">Paid now</option>
                </select>
                <select
                  className="h-8 rounded-lg border border-border bg-card px-2 text-[12px]"
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value)}
                >
                  {/* The payment ledger's own modes (utils/billingPayments.js) — a "Paid now" is
                      a real bill_payments row. An insurer-covered test is "Payment pending". */}
                  <option>Cash</option><option>Card</option><option>UPI</option>
                </select>
              </div>
            )}
          </div>
          )}

          {error && <ErrorState message={error} />}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>Cancel</Button>
          <Button onClick={submit} disabled={create.isPending || !patient || nothingSelected || noEncounter}>
            {create.isPending ? 'Placing order…' : 'Place order'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
